import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaFileAlt, FaSearch, FaEdit } from 'react-icons/fa';
import './ChargesManagement.css';

const ChargesManagement = () => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [editingCharge, setEditingCharge] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    fetchCharges();
  }, [pagination.page, searchTerm]);

  const fetchCharges = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/charges', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm
        }
      });

      setCharges(response.data.data);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (error) {
      console.error('Error fetching charges:', error);
      alert('Failed to load charges');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleEdit = (charge) => {
    setEditingCharge({
      ...charge,
      mcaCode: charge.mca_code,
      recommendedBond: charge.recommended_bond,
      minFine: charge.min_fine,
      maxFine: charge.max_fine,
      minJail: charge.min_jail,
      maxJail: charge.max_jail,
      penaltySection: charge.penalty_section
    });
    setShowEditForm(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/charges/${editingCharge.id}`, editingCharge);
      alert('Charge updated successfully!');
      setShowEditForm(false);
      setEditingCharge(null);
      fetchCharges();
    } catch (error) {
      console.error('Error updating charge:', error);
      alert('Failed to update charge');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return `$${parseFloat(value).toLocaleString()}`;
  };

  if (loading && charges.length === 0) {
    return <div className="loading">Loading charges...</div>;
  }

  return (
    <div className="charges-management">
      <div className="charges-header">
        <h1><FaFileAlt /> Montana Charges</h1>
        <div className="charges-stats">
          <span>Total Charges: {pagination.total}</span>
        </div>
      </div>

      <div className="charges-controls">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by MCA code or description..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {showEditForm && editingCharge && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Charge</h2>
              <button className="close-btn" onClick={() => setShowEditForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveEdit} className="edit-form">
              <div className="form-group">
                <label>MCA Code</label>
                <input
                  type="text"
                  value={editingCharge.mcaCode}
                  onChange={(e) => setEditingCharge({...editingCharge, mcaCode: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editingCharge.description}
                  onChange={(e) => setEditingCharge({...editingCharge, description: e.target.value})}
                  rows="3"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Degree</label>
                  <input
                    type="text"
                    value={editingCharge.degree || ''}
                    onChange={(e) => setEditingCharge({...editingCharge, degree: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Recommended Bond</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingCharge.recommendedBond || ''}
                    onChange={(e) => setEditingCharge({...editingCharge, recommendedBond: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Min Fine</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingCharge.minFine || ''}
                    onChange={(e) => setEditingCharge({...editingCharge, minFine: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Max Fine</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingCharge.maxFine || ''}
                    onChange={(e) => setEditingCharge({...editingCharge, maxFine: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Min Jail</label>
                  <input
                    type="text"
                    value={editingCharge.minJail || ''}
                    onChange={(e) => setEditingCharge({...editingCharge, minJail: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Max Jail</label>
                  <input
                    type="text"
                    value={editingCharge.maxJail || ''}
                    onChange={(e) => setEditingCharge({...editingCharge, maxJail: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="charges-table-container">
        <table className="charges-table">
          <thead>
            <tr>
              <th>MCA Code</th>
              <th>Description</th>
              <th>Degree</th>
              <th>Recommended Bond</th>
              <th>Fine Range</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {charges.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {searchTerm ? 'No charges found matching your search' : 'No charges available'}
                </td>
              </tr>
            ) : (
              charges.map(charge => (
                <tr key={charge.id}>
                  <td className="code-cell">{charge.mca_code}</td>
                  <td className="description-cell">{charge.description}</td>
                  <td>
                    <span className={`degree-badge degree-${(charge.degree || '').toLowerCase()}`}>
                      {charge.degree || 'N/A'}
                    </span>
                  </td>
                  <td>{formatCurrency(charge.recommended_bond)}</td>
                  <td>
                    {charge.min_fine && charge.max_fine
                      ? `${formatCurrency(charge.min_fine)} - ${formatCurrency(charge.max_fine)}`
                      : 'N/A'}
                  </td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(charge)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn-secondary"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span className="page-info">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="btn-secondary"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ChargesManagement;
